/**
 * Pedagogical Checks — Shared pure-check module (Phase 1b, Strak-Trek Refactor).
 *
 * SSOT for the three pedagogical checks that BOTH the build-time pipeline
 * (`src/governance/pipeline/stages/pedagogyStage.ts`) and the runtime guard
 * (`src/practice/logic/pedagogicalGuard.ts`) need to run.
 *
 * Each check is a PURE function:
 *   - No side effects
 *   - No imports from the pipeline issue contract (no ValidationIssue)
 *   - No imports from the runtime guard
 *   - Returns a CheckResult that callers translate into their own output shape
 *
 * The two callers adapt the results differently:
 *   - Pipeline wraps the CheckResult into a ValidationIssue via blocker()/coach()
 *   - Guard maps CheckResult.code → legacy GuardVerdict.reason string
 *
 * Tier-band & DOK floor numerics already live in `governance/tierBands.ts`
 * (Sprint 4 / R1) — this module imports them and adds no new SSOTs.
 *
 * ⚠️ MAINTENANCE CONTRACT
 * Adding a new shared pedagogical check means:
 *   1. Add the pure check here.
 *   2. Add the IssueCode in `governance/pipeline/issues/codes.ts`.
 *   3. Wire it into `pedagogyStage.ts` (build-time gate).
 *   4. Wire it into `pedagogicalGuard.ts` (runtime safety net).
 *   5. Add the IssueCode → guard-reason mapping in pedagogicalGuard.ts.
 */

import type { Question } from '../../content'
import { getMinDokForBand, getTierBand } from '../tierBands'
import { ISSUE_CODES, type IssueCode } from '../pipeline/issues/codes'
import { SEVERITY } from '../pipeline/severityScale'

// ─── CheckResult Contract ─────────────────────────────────────────────────────

/**
 * Result of a single pure pedagogical check.
 *
 * - `passed: true`  → the question satisfies the check; other fields irrelevant.
 * - `passed: false` → the check FOUND a violation. `code`, `reason`, `severity`
 *                     and `isFatal` describe it; callers turn this into their
 *                     own output (ValidationIssue for pipeline, GuardVerdict
 *                     for runtime guard).
 *
 * `severity` is the same 0-100 numeric scale used by ValidationIssue.severity.
 * `isFatal` mirrors the runtime guard's fatal/soft distinction:
 *   - fatal → guard rejects the question (replace if possible)
 *   - soft  → guard allows the question through but logs telemetry
 *
 * `metadata` carries check-specific details (the offending aid, indicator
 * value, distractor count, etc.) so the pipeline adapter can build a
 * fully-detailed ValidationIssue without re-computing anything.
 */
export interface CheckResult {
  passed: boolean
  code?: IssueCode
  reason?: string
  severity?: number
  isFatal?: boolean
  metadata?: Record<string, unknown>
}

const PASS: CheckResult = { passed: true }

// ─── Check 1: DOK Floor (tier-DOK mismatch) ──────────────────────────────────

/**
 * Fail if the question carries a `pedagogy.dok` that is BELOW the minimum
 * DOK required by its tier band.
 *
 * NB: this check ONLY fires when `pedagogy.dok` is defined. Missing-DOK
 * handling is pipeline-only (PEDAGOGY_DOK_MISSING) and is not part of the
 * shared subset because the runtime guard cannot meaningfully act on it.
 *
 * Maps to ISSUE_CODES.PEDAGOGY_DOK_TOO_LOW.
 * Guard reason string (legacy, telemetry-stable): `'tier-dok-mismatch'`.
 */
export function checkDokFloor(question: Question): CheckResult {
  const dok = question.pedagogy?.dok
  if (dok === undefined) return PASS

  const tier = question.tier ?? 1
  const band = getTierBand(tier)
  // P4 (mei 2026): module-aware floor — TAA expert tier accepts DOK 2
  // for begrip-tasks (CONCLUSIE/STRUCTUUR/VERB). See tierBands.ts.
  const minDok = getMinDokForBand(band, question.module)

  if (dok >= minDok) return PASS

  return {
    passed: false,
    code: ISSUE_CODES.PEDAGOGY_DOK_TOO_LOW,
    reason: `Vraag ${question.id} heeft DOK ${dok}, maar tier-band '${band}' vereist minimaal DOK ${minDok}.`,
    severity: SEVERITY.NEAR_CRITICAL,
    isFatal: true,
    metadata: {
      dok,
      minDok,
      tierBand: band,
      tier,
      module: question.module,
    },
  }
}

// ─── Check 2: Indicator-Answer Parity ────────────────────────────────────────

/**
 * Fail if any stimulus/support aid carries an `indicatorValue` that
 * trivially reveals the correct answer (exact string match OR numeric
 * proximity within the configured tolerance, default 5%).
 *
 * `pedagogy.mode === 'instruction'` is exempt by design: instruction visuals
 * are allowed to show the target.
 *
 * Maps to ISSUE_CODES.PEDAGOGY_INDICATOR_LEAKS_ANSWER.
 * Guard reason string (legacy, telemetry-stable): `'indicator-trivial'`.
 *
 * NOTE: this pure check restores the string-match branch that the runtime
 * guard was previously missing — it only did numeric proximity. Bringing
 * the guard onto this module fixes that asymmetry as a side effect.
 */
export function checkIndicatorParity(question: Question): CheckResult {
  const mode = question.pedagogy?.mode ?? 'practice'
  if (mode === 'instruction') return PASS

  const tolerance = question.pedagogy?.indicatorParityTolerance ?? 0.05
  const allAids = [
    ...(question.stimulusAids ?? []),
    ...(question.supportAids ?? []),
  ]

  for (const aid of allAids) {
    const indicatorValue = aid.indicatorValue
    if (indicatorValue === undefined) continue

    const indicatorStr = String(indicatorValue).trim().toLowerCase()

    // Resolve the correct-answer text representation.
    // - MC stores correctAnswer as an option index → look up option text.
    // - Otherwise treat correctAnswer as a direct value.
    const correctAnswer = question.correctAnswer
    let correctStr: string | null = null
    let correctNum: number | null = null

    if (typeof correctAnswer === 'number') {
      if (question.options && correctAnswer < question.options.length) {
        const optText = question.options[correctAnswer]
        if (optText !== undefined) {
          correctStr = String(optText).trim().toLowerCase()
          const parsed = parseFloat(correctStr.replace(',', '.'))
          if (!isNaN(parsed)) correctNum = parsed
        }
      }
      if (correctStr === null) {
        correctStr = String(correctAnswer).trim().toLowerCase()
        correctNum = correctAnswer
      }
    } else if (typeof correctAnswer === 'string') {
      correctStr = correctAnswer.trim().toLowerCase()
      const parsed = parseFloat(correctStr.replace(',', '.'))
      if (!isNaN(parsed)) correctNum = parsed
    }

    if (correctStr === null) continue

    // 2a. Direct string match
    if (indicatorStr === correctStr) {
      return {
        passed: false,
        code: ISSUE_CODES.PEDAGOGY_INDICATOR_LEAKS_ANSWER,
        reason: `Visueel hulpmiddel (${aid.type}) toont indicatorwaarde "${indicatorValue}" die exact overeenkomt met het correcte antwoord.`,
        severity: SEVERITY.CRITICAL,
        isFatal: true,
        metadata: {
          aidType: aid.type,
          indicatorValue,
          correctAnswer: correctStr,
          matchKind: 'exact-string',
        },
      }
    }

    // 2b. Numeric proximity within tolerance
    const indicatorNum = parseFloat(indicatorStr.replace(',', '.'))
    if (!isNaN(indicatorNum) && correctNum !== null && !isNaN(correctNum)) {
      const range = Math.abs(correctNum) || 1
      const relDiff = Math.abs(indicatorNum - correctNum) / range
      if (relDiff < tolerance) {
        return {
          passed: false,
          code: ISSUE_CODES.PEDAGOGY_INDICATOR_LEAKS_ANSWER,
          reason: `Visueel hulpmiddel (${aid.type}) toont indicatorwaarde ${indicatorValue} die binnen ${(tolerance * 100).toFixed(0)}% van het correcte antwoord ligt.`,
          severity: SEVERITY.CRITICAL,
          isFatal: true,
          metadata: {
            aidType: aid.type,
            indicatorValue,
            correctAnswer: correctNum,
            relDiff,
            tolerance,
            matchKind: 'numeric-proximity',
          },
        }
      }
    }
  }

  return PASS
}

// ─── Check 3: Distractors Untagged (high-stakes MC) ──────────────────────────

/**
 * Fail if a multiple_choice question at tier ≥ 9 has wrong-answer options
 * but ZERO misconception tags on `pedagogy.misconceptionTags`.
 *
 * This is the high-stakes-only subset of the pipeline's richer distractor-
 * tagging check. The pipeline additionally flags partial tagging, enforce-
 * flag-driven blocking for all tiers, and unknown tag IDs — those branches
 * remain pipeline-only because the runtime guard cannot act on them.
 *
 * Maps to ISSUE_CODES.PEDAGOGY_DISTRACTORS_UNTAGGED.
 * Guard reason string (legacy, telemetry-stable): `'distractors-untagged'`.
 *
 * Marked `isFatal: false` (soft) — guard allows the question through but
 * logs it; pipeline still treats it as a blocker via its own wrapping.
 */
export function checkDistractorsUntagged(question: Question): CheckResult {
  if (question.questionType !== 'multiple_choice') return PASS

  const tier = question.tier ?? 1
  if (tier < 9) return PASS

  const tags = question.pedagogy?.misconceptionTags ?? []
  const wrongCount = (question.options?.length ?? 0) - 1
  if (wrongCount <= 0) return PASS
  if (tags.length > 0) return PASS

  return {
    passed: false,
    code: ISSUE_CODES.PEDAGOGY_DISTRACTORS_UNTAGGED,
    reason: `Vraag ${question.id} heeft ${wrongCount} afleiders zonder misconceptie-tags.`,
    severity: SEVERITY.HIGH,
    isFatal: false,
    metadata: {
      tier,
      distractorCount: wrongCount,
      tagCount: 0,
    },
  }
}

// ─── Check 4: Dead optionIndex on misconceptionTags (S-2, shadow-mode) ──────
//
// ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §F0.B / Q2Q-REK Batch 3.
// `pedagogy.misconceptionTags[].optionIndex` only makes sense for
// multiple_choice questions: it tells the support layer which option the
// tag describes. On fill_in / step_solver / strategy_solver / ordering /
// categorize / etc. the index points to a non-existent options[] array —
// dead metadata copied from MC templates by AI-generated seeds.
//
// Maps to ISSUE_CODES.PEDAGOGY_DEAD_OPTION_INDEX. Always emitted as `info`
// by the pipeline adapter (never blocks). Cross-module — fires for any
// question whose type lacks options[].
//
// PURE: same as the other checks here, no side effects. The pipeline
// stage adapter (pedagogyStage.ts) wraps the CheckResult into an
// info-level ValidationIssue.

export function checkDeadOptionIndex(question: Question): CheckResult {
  // Only flag NON-MC types — MC questions legitimately use optionIndex.
  if (question.questionType === 'multiple_choice') return PASS

  const tags = question.pedagogy?.misconceptionTags
  if (!Array.isArray(tags) || tags.length === 0) return PASS

  const offending = tags
    .map((t, i) => ({ idx: i, tag: t }))
    .filter(
      ({ tag }) =>
        tag &&
        typeof tag === 'object' &&
        typeof (tag as { optionIndex?: unknown }).optionIndex === 'number',
    )

  if (offending.length === 0) return PASS

  return {
    passed: false,
    code: ISSUE_CODES.PEDAGOGY_DEAD_OPTION_INDEX,
    reason: `Vraag ${question.id} (type '${question.questionType}') heeft ${offending.length} misconceptie-tag(s) met dood 'optionIndex' veld — dit veld is alleen zinvol voor multiple_choice.`,
    severity: SEVERITY.COSMETIC,
    isFatal: false,
    metadata: {
      questionType: question.questionType,
      offendingTagIndices: offending.map((o) => o.idx),
      offendingTagIds: offending.map(
        (o) => (o.tag as { id?: string }).id ?? null,
      ),
    },
  }
}
